// hooks/useRoles.ts - VERSIÓN CORREGIDA
import { useState, useEffect, useCallback, useMemo } from "react";
import { Role } from "@/types/roles";
import { rolesService } from "@/services/roles";
import { useAuth } from "./useAuth";

export function useRoles() {
  const { user, company, can } = useAuth(); // SOLO 'can', no 'hasPermission'

  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Cargar roles
  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();

    const fetchRoles = async () => {
      if (!company?.slug) {
        console.log("No company slug available");
        return;
      }

      if (mounted) {
        try {
          setLoading(true);
          setError(null);

          console.log(`📡 Obteniendo roles para empresa: ${company.slug}`);
          const response = await rolesService.getRoles(company.slug);

          if (mounted) {
            const rolesData = response.data || [];
            console.log(`✅ Roles obtenidos: ${rolesData.length}`);
            setRoles(rolesData);
          }
        } catch (err: any) {
          if (mounted) {
            console.error("❌ Error obteniendo roles:", err);
            setError(err.message || "Error al cargar los roles");
            setRoles([]);
          }
        } finally {
          if (mounted) {
            setLoading(false);
          }
        }
      }
    };

    fetchRoles();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [company?.slug]);

  // Filtrar roles
  const filteredRoles = useMemo(() => {
    console.log("Filtering roles, total:", roles.length);

    if (!searchQuery && statusFilter === "all") {
      return roles;
    }

    let filtered = roles;

    // Filtrar por búsqueda
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (role) =>
          role.name.toLowerCase().includes(query) ||
          (role.description?.toLowerCase() || "").includes(query) ||
          (role.user_type?.toLowerCase() || "").includes(query),
      );
    }

    // Filtrar por estado
    if (statusFilter !== "all") {
      switch (statusFilter) {
        case "active":
          filtered = filtered.filter((role) => role.is_active !== false);
          break;
        case "inactive":
          filtered = filtered.filter((role) => role.is_active === false);
          break;
        case "system":
          filtered = filtered.filter((role) => role.is_system_role);
          break;
        case "custom":
          filtered = filtered.filter((role) => !role.is_system_role);
          break;
      }
    }

    return filtered;
  }, [roles, searchQuery, statusFilter]);

  // Estadísticas
  const statistics = useMemo(() => {
    const activeRoles = roles.filter((role) => role.is_active !== false).length;
    const systemRoles = roles.filter((role) => role.is_system_role).length;
    const totalUsers = roles.reduce(
      (sum, role) => sum + (role.users_count || 0),
      0,
    );

    return {
      totalRoles: roles.length,
      activeRoles,
      totalUsers,
      systemRoles,
      customRoles: roles.length - systemRoles,
    };
  }, [roles]);

  // Crear rol
  const createRole = useCallback(
    async (roleData: Partial<Role>) => {
      if (!company?.id || !company?.slug) {
        throw new Error("No hay compañía seleccionada");
      }

      try {
        setLoading(true);
        console.log("📝 Creando nuevo rol...", roleData.name);

        const response = await rolesService.createRole({
          slug: company.slug,
          userId: user?.id,
          name: roleData.name || "",
          description: roleData.description || "",
          permissions: Array.isArray(roleData.permissions)
            ? roleData.permissions
            : [],
        });

        // Actualizar lista de roles
        setRoles((prev) => {
          const newRoles = [...prev, response.data];
          console.log("✅ Rol creado, total:", newRoles.length);
          return newRoles;
        });

        return response.data;
      } catch (err: any) {
        console.error("❌ Error creando rol:", err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [company?.slug, user?.id],
  );

  // Actualizar rol
  const updateRole = useCallback(
    async (roleId: string, roleData: Partial<Role>) => {
      try {
        setLoading(true);
        console.log("📝 Actualizando rol...", roleId);

        const response = await rolesService.updateRole(roleId, {
          slug: company?.slug,
          userId: user?.id,
          name: roleData.name,
          description: roleData.description,
          permissions: Array.isArray(roleData.permissions)
            ? roleData.permissions
            : [],
        });

        // Actualizar en la lista
        setRoles((prev) =>
          prev.map((role) => (role.id === roleId ? response.data : role)),
        );

        console.log("✅ Rol actualizado");
        return response.data;
      } catch (err: any) {
        console.error("❌ Error actualizando rol:", err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [company?.slug, user?.id],
  );

  // Eliminar rol
  const deleteRole = useCallback(
    async (roleToDelete: Role) => {
      if (!company?.slug || !user?.id) return;

      try {
        setLoading(true);
        console.log("🗑️ Eliminando rol...", roleToDelete.name);

        await rolesService.deleteRole(roleToDelete.id, company.slug, user.id);

        // Remover de la lista
        setRoles((prev) => {
          const newRoles = prev.filter((role) => role.id !== roleToDelete.id);
          console.log("✅ Rol eliminado, restantes:", newRoles.length);
          return newRoles;
        });
      } catch (err: any) {
        console.error("❌ Error eliminando rol:", err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [company?.slug, user?.id],
  );

  // Verificar permisos usando 'can' de useAuth
  const canManageRoles = useMemo(() => {
    return can("roles.manage") || can("all.view") || can("roles.create");
  }, [can]);

  // Función para forzar recarga
  const refreshRoles = useCallback(async () => {
    if (!company?.slug) return;

    try {
      setLoading(true);
      const response = await rolesService.getRoles(company.slug);
      setRoles(response.data || []);
    } catch (err: any) {
      console.error("Error refrescando roles:", err);
    } finally {
      setLoading(false);
    }
  }, [company?.slug]);

  return {
    roles,
    filteredRoles,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    createRole,
    updateRole,
    deleteRole,
    statistics,
    canManageRoles,
    refreshRoles,
  };
}
